/*
 * CORSAILLE LIVING WATER — Step 1 / standalone signature engine
 * Version 1.0.0
 *
 * Two fully rendered states coexist throughout every transition. The incoming
 * state is uncovered exactly behind a continuously evolving water front.
 * A narrow refractive band, a true backdrop wet film and a procedural GPU
 * highlight layer make the surface read as transparent water rather than a
 * decorative wipe. No external libraries. No video. No frame sequence.
 */
(() => {
  'use strict';

  // The hero and Signature Stage can coexist on the same template. Shopify may
  // therefore render this asset tag more than once; only one engine instance
  // may own global lifecycle listeners. Later executions simply initialize any
  // newly rendered sections and exit.
  if (window.CorsailleLivingWater?.version) {
    window.CorsailleLivingWater.initialize?.(document);
    return;
  }

  const SELECTORS = {
    stage: '[data-cwl-stage]',
    hero: '[data-cwl-hero]',
    slide: '[data-cwl-slide]',
    viewport: '[data-cwl-viewport]',
    canvas: '[data-cwl-water-canvas]',
    film: '[data-cwl-water-film]',
    refract: '[data-cwl-refract-stack]',
    revealPath: '[data-cwl-clip-reveal]',
    oldBandPath: '[data-cwl-clip-old-band]',
    newBandPath: '[data-cwl-clip-new-band]',
    wetPath: '[data-cwl-clip-wet]',
    dot: '[data-cwl-dot]',
    previous: '[data-cwl-previous]',
    next: '[data-cwl-next]',
    live: '[data-cwl-live]'
  };

  const clamp = (value, min = 0, max = 1) => Math.min(max, Math.max(min, value));
  const lerp = (from, to, amount) => from + (to - from) * amount;
  const smoothstep = (edge0, edge1, value) => {
    const x = clamp((value - edge0) / Math.max(0.000001, edge1 - edge0));
    return x * x * (3 - 2 * x);
  };
  const smootherstep = (value) => {
    const x = clamp(value);
    return x * x * x * (x * (x * 6 - 15) + 10);
  };
  const fluidEase = (value) => {
    const x = clamp(value);
    // Symmetric physical travel: at half time the water has covered half the card.
    // A small linear component prevents the first and final frames feeling stalled.
    return lerp(x, smootherstep(x), 0.82);
  };
  const normalizeIndex = (index, length) => ((index % length) + length) % length;
  const reducedMotion = () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  const saveData = () => navigator.connection?.saveData === true;
  const deviceMemory = () => Number(navigator.deviceMemory || 4);
  const useSimpleTransition = () => saveData() || deviceMemory() <= 2;
  const IMAGE_DECODE_TIMEOUT = 4200;
  const uid = (() => {
    let counter = 0;
    return (prefix = 'cwl') => `${prefix}-${Date.now().toString(36)}-${(++counter).toString(36)}`;
  })();

  function hash1(value, seed) {
    const x = Math.sin(value * 127.1 + seed * 311.7) * 43758.5453123;
    return x - Math.floor(x);
  }

  function valueNoise1D(value, seed) {
    const base = Math.floor(value);
    const fraction = value - base;
    const eased = fraction * fraction * (3 - 2 * fraction);
    return lerp(hash1(base, seed), hash1(base + 1, seed), eased) * 2 - 1;
  }

  function fbm1D(value, seed) {
    let sum = 0;
    let amplitude = 0.56;
    let frequency = 1;
    for (let octave = 0; octave < 4; octave += 1) {
      sum += valueNoise1D(value * frequency, seed + octave * 17.19) * amplitude;
      frequency *= 2.08;
      amplitude *= 0.47;
    }
    return sum;
  }

  function gaussian(value, center, width) {
    const distance = (value - center) / Math.max(0.001, width);
    return Math.exp(-distance * distance * 0.5);
  }

  class FluidFront {
    constructor(sampleCount, intensity = 1) {
      this.sampleCount = sampleCount;
      this.intensity = intensity;
      this.samples = new Float32Array(sampleCount);
    }

    generate(progress, elapsedSeconds, direction, seed) {
      const p = clamp(progress);
      const base = direction > 0 ? lerp(-0.135, 1.135, p) : lerp(1.135, -0.135, p);
      const travelPresence = Math.sin(Math.PI * smoothstep(0.01, 0.99, p));
      const amplitude = (0.028 + 0.026 * travelPresence) * this.intensity;
      const tilt = direction * (0.245 + 0.07 * Math.sin(seed * 2.17));
      const phase = elapsedSeconds * 0.43 + seed * 1.73;
      const tongueCenter = 0.22 + hash1(4.1, seed) * 0.56;
      const hollowCenter = 0.18 + hash1(8.7, seed) * 0.64;
      const tongueStrength = (0.022 + hash1(12.2, seed) * 0.022) * this.intensity;
      const hollowStrength = (0.013 + hash1(15.8, seed) * 0.016) * this.intensity;

      for (let index = 0; index < this.sampleCount; index += 1) {
        const y = index / (this.sampleCount - 1);
        const broad = fbm1D(y * 2.25 + phase * 0.08, seed + 1.9);
        const medium = fbm1D(y * 5.3 - phase * 0.13, seed + 7.4) * 0.46;
        const fine = fbm1D(y * 13.7 + phase * 0.17, seed + 14.2) * 0.16;
        const calmUndulation = Math.sin(y * Math.PI * 2.15 + phase * 0.31) * 0.26;
        const tongue = gaussian(y, tongueCenter + Math.sin(phase * 0.29) * 0.018, 0.105) * tongueStrength;
        const hollow = gaussian(y, hollowCenter + Math.cos(phase * 0.23) * 0.014, 0.082) * hollowStrength;
        const taper = 0.72 + Math.sin(Math.PI * y) * 0.28;
        const irregular = (broad + medium + fine + calmUndulation) * amplitude * taper;
        const localShape = direction * (tongue - hollow);
        this.samples[index] = base + (y - 0.5) * tilt + irregular + localShape;
      }
      return this.samples;
    }
  }

  function pointsFromSamples(samples, offset = 0, orientation = 'vertical') {
    const points = [];
    const last = samples.length - 1;
    for (let index = 0; index <= last; index += 1) {
      const along = index / last;
      if (orientation === 'horizontal') points.push({ x: along, y: samples[index] + offset });
      else points.push({ x: samples[index] + offset, y: along });
    }
    return points;
  }

  function curvePath(points, move = true, reverse = false) {
    const source = reverse ? [...points].reverse() : points;
    if (!source.length) return '';
    let path = move ? `M ${source[0].x.toFixed(5)} ${source[0].y.toFixed(5)}` : '';
    for (let index = 0; index < source.length - 1; index += 1) {
      const p0 = source[index - 1] || source[index];
      const p1 = source[index];
      const p2 = source[index + 1];
      const p3 = source[index + 2] || p2;
      const cp1x = p1.x + (p2.x - p0.x) / 6;
      const cp1y = p1.y + (p2.y - p0.y) / 6;
      const cp2x = p2.x - (p3.x - p1.x) / 6;
      const cp2y = p2.y - (p3.y - p1.y) / 6;
      path += ` C ${cp1x.toFixed(5)} ${cp1y.toFixed(5)} ${cp2x.toFixed(5)} ${cp2y.toFixed(5)} ${p2.x.toFixed(5)} ${p2.y.toFixed(5)}`;
    }
    return path;
  }

  function revealPath(samples, direction, offset = 0, orientation = 'vertical') {
    const boundary = pointsFromSamples(samples, offset, orientation);
    if (orientation === 'horizontal') {
      const edge = direction > 0 ? 0 : 1;
      return `M 0 ${edge} L 1 ${edge} L 1 ${boundary[boundary.length - 1].y.toFixed(5)}${curvePath(boundary, false, true)} Z`;
    }
    const edge = direction > 0 ? 0 : 1;
    return `M ${edge} 0 L ${boundary[0].x.toFixed(5)} 0${curvePath(boundary, false)} L ${edge} 1 Z`;
  }

  function bandPath(samples, offsetA, offsetB, orientation = 'vertical') {
    const first = pointsFromSamples(samples, offsetA, orientation);
    const second = pointsFromSamples(samples, offsetB, orientation);
    if (orientation === 'horizontal') {
      return `${curvePath(first, true)} L 1 ${second[second.length - 1].y.toFixed(5)}${curvePath(second, false, true)} Z`;
    }
    return `${curvePath(first, true)} L ${second[second.length - 1].x.toFixed(5)} 1${curvePath(second, false, true)} Z`;
  }

  function fullPath() {
    return 'M 0 0 H 1 V 1 H 0 Z';
  }

  function emptyPath() {
    return 'M 0 0 H 0 V 1 H 0 Z';
  }

  function drainagePath(drainProgress, elapsedSeconds, seed) {
    const count = 48;
    const boundary = [];
    const base = lerp(-0.09, 1.09, smootherstep(drainProgress));
    for (let index = 0; index < count; index += 1) {
      const x = index / (count - 1);
      const broad = fbm1D(x * 2.9 + elapsedSeconds * 0.08, seed + 32.7) * 0.025;
      const fine = fbm1D(x * 9.4 - elapsedSeconds * 0.12, seed + 41.4) * 0.009;
      const slope = (x - 0.5) * 0.035;
      boundary.push({ x, y: base + broad + fine + slope });
    }
    let path = `M 0 ${boundary[0].y.toFixed(5)}`;
    for (let index = 0; index < boundary.length - 1; index += 1) {
      const p0 = boundary[index - 1] || boundary[index];
      const p1 = boundary[index];
      const p2 = boundary[index + 1];
      const p3 = boundary[index + 2] || p2;
      const cp1x = p1.x + (p2.x - p0.x) / 6;
      const cp1y = p1.y + (p2.y - p0.y) / 6;
      const cp2x = p2.x - (p3.x - p1.x) / 6;
      const cp2y = p2.y - (p3.y - p1.y) / 6;
      path += ` C ${cp1x.toFixed(5)} ${cp1y.toFixed(5)} ${cp2x.toFixed(5)} ${cp2y.toFixed(5)} ${p2.x.toFixed(5)} ${p2.y.toFixed(5)}`;
    }
    return `${path} L 1 1 L 0 1 Z`;
  }

  function decodeImage(image, timeout = IMAGE_DECODE_TIMEOUT) {
    if (!image) return Promise.resolve(false);

    return new Promise((resolve) => {
      let settled = false;
      let timer = 0;

      const finish = (decoded) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        image.removeEventListener('load', onLoad);
        image.removeEventListener('error', onError);
        resolve(decoded);
      };
      const decodeReadyImage = () => {
        if (image.naturalWidth <= 0) {
          finish(false);
          return;
        }
        try {
          const pending = image.decode?.();
          if (pending?.then) pending.then(() => finish(true), () => finish(true));
          else finish(true);
        } catch (_) {
          finish(true);
        }
      };
      const onLoad = () => decodeReadyImage();
      const onError = () => finish(false);

      timer = window.setTimeout(() => finish(false), Math.max(250, Number(timeout) || IMAGE_DECODE_TIMEOUT));
      if (image.complete) decodeReadyImage();
      else {
        image.addEventListener('load', onLoad, { once: true });
        image.addEventListener('error', onError, { once: true });
      }
    });
  }

  function decodeSlide(slide, { eager = false } = {}) {
    if (!slide) return Promise.resolve([]);
    const images = [...slide.querySelectorAll('img')];
    if (eager) {
      images.forEach((image) => {
        image.loading = 'eager';
        image.fetchPriority = 'high';
      });
    }
    return Promise.all(images.map((image) => decodeImage(image)));
  }

  function stripCloneAccessibility(clone) {
    clone.removeAttribute('id');
    clone.removeAttribute('data-shopify-block-id');
    clone.setAttribute('aria-hidden', 'true');
    clone.setAttribute('inert', '');
    clone.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));
    clone.querySelectorAll('a,button,input,select,textarea,[tabindex]').forEach((element) => {
      element.setAttribute('tabindex', '-1');
      element.removeAttribute('name');
    });
    return clone;
  }

  function setSlideInteractive(slide, interactive) {
    slide.toggleAttribute('inert', !interactive);
    slide.setAttribute('aria-hidden', interactive ? 'false' : 'true');
  }

  function accentRGB(name) {
    switch ((name || '').toLowerCase()) {
      case 'blue': return [0.50, 0.72, 0.83];
      case 'pink': return [0.88, 0.67, 0.70];
      case 'champagne': return [0.84, 0.74, 0.60];
      default: return [0.92, 0.94, 0.95];
    }
  }

  class Canvas2DWater {
    constructor(canvas) {
      this.canvas = canvas;
      this.context = canvas.getContext('2d', { alpha: true, desynchronized: true });
      this.width = 0;
      this.height = 0;
      this.dpr = 1;
    }

    resize(width, height) {
      if (!this.context) return;
      const cap = window.innerWidth < 750 ? 1.25 : 1.5;
      this.dpr = Math.min(window.devicePixelRatio || 1, cap);
      this.width = Math.max(1, Math.round(width * this.dpr));
      this.height = Math.max(1, Math.round(height * this.dpr));
      if (this.canvas.width !== this.width || this.canvas.height !== this.height) {
        this.canvas.width = this.width;
        this.canvas.height = this.height;
      }
      this.canvas.style.width = `${width}px`;
      this.canvas.style.height = `${height}px`;
    }

    render(state) {
      const ctx = this.context;
      if (!ctx) return;
      const { samples, direction, phase, drain, accent, orientation = 'vertical' } = state;
      const width = this.width;
      const height = this.height;
      ctx.clearRect(0, 0, width, height);
      ctx.save();
      ctx.scale(this.dpr, this.dpr);
      const cssWidth = width / this.dpr;
      const cssHeight = height / this.dpr;
      const rgb = accentRGB(accent).map((value) => Math.round(value * 255));

      if (phase === 'cross') {
        const path = new Path2D();
        for (let index = 0; index < samples.length; index += 1) {
          const along = index / (samples.length - 1);
          const x = orientation === 'horizontal' ? along * cssWidth : samples[index] * cssWidth;
          const y = orientation === 'horizontal' ? samples[index] * cssHeight : along * cssHeight;
          if (index === 0) path.moveTo(x, y);
          else path.lineTo(x, y);
        }
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.filter = 'blur(7px)';
        ctx.globalAlpha = 0.28;
        ctx.strokeStyle = 'rgba(12,12,12,.74)';
        ctx.lineWidth = 24;
        ctx.stroke(path);
        ctx.filter = 'blur(2.4px)';
        ctx.globalAlpha = 0.78;
        ctx.strokeStyle = `rgba(${rgb.join(',')},.98)`;
        ctx.lineWidth = 8.5;
        ctx.stroke(path);
        ctx.filter = 'none';
        ctx.globalAlpha = 0.96;
        ctx.strokeStyle = 'rgba(255,255,255,.98)';
        ctx.lineWidth = 1.8;
        ctx.stroke(path);
      } else {
        const y = lerp(-0.08, 1.08, smootherstep(drain)) * cssHeight;
        const gradient = ctx.createLinearGradient(0, y - 55, 0, y + 24);
        gradient.addColorStop(0, 'rgba(255,255,255,0)');
        gradient.addColorStop(0.62, `rgba(${rgb.join(',')},.22)`);
        gradient.addColorStop(1, 'rgba(255,255,255,0)');
        ctx.globalAlpha = 1 - drain;
        ctx.fillStyle = gradient;
        ctx.fillRect(0, y - 72, cssWidth, 110);
      }
      ctx.restore();
    }

    clear() {
      this.context?.clearRect(0, 0, this.canvas.width, this.canvas.height);
    }
  }

  class WebGLWater {
    constructor(canvas) {
      this.canvas = canvas;
      this.gl = canvas.getContext('webgl', {
        alpha: true,
        antialias: true,
        premultipliedAlpha: false,
        powerPreference: 'high-performance',
        preserveDrawingBuffer: false
      });
      if (!this.gl) throw new Error('WebGL unavailable');
      this.width = 0;
      this.height = 0;
      this.dpr = 1;
      this.frontData = null;
      this.frontTexture = null;
      this.program = null;
      this.uniforms = {};
      this.initialize();
    }

    compile(type, source) {
      const gl = this.gl;
      const shader = gl.createShader(type);
      gl.shaderSource(shader, source);
      gl.compileShader(shader);
      if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
        const message = gl.getShaderInfoLog(shader);
        gl.deleteShader(shader);
        throw new Error(message || 'Shader compilation failed');
      }
      return shader;
    }

    initialize() {
      const gl = this.gl;
      const vertex = this.compile(gl.VERTEX_SHADER, `
        attribute vec2 a_position;
        varying vec2 v_uv;
        void main(){
          v_uv = a_position * 0.5 + 0.5;
          gl_Position = vec4(a_position, 0.0, 1.0);
        }
      `);
      const fragment = this.compile(gl.FRAGMENT_SHADER, `
        precision highp float;
        varying vec2 v_uv;
        uniform sampler2D u_front;
        uniform vec2 u_resolution;
        uniform float u_time;
        uniform float u_direction;
        uniform float u_phase;
        uniform float u_drain;
        uniform float u_intensity;
        uniform float u_orientation;
        uniform float u_frontSize;
        uniform vec3 u_accent;

        float decodePacked(vec4 texel){
          float high = floor(texel.r * 255.0 + 0.5);
          float low = floor(texel.g * 255.0 + 0.5);
          return (high * 256.0 + low) / 65535.0 * 1.5 - 0.25;
        }

        float decodeFront(float y){
          float size = max(2.0, u_frontSize);
          float position = clamp(y, 0.0, 1.0) * (size - 1.0);
          float lowerIndex = floor(position);
          float upperIndex = min(size - 1.0, lowerIndex + 1.0);
          float blend = smoothstep(0.0, 1.0, fract(position));
          float lowerY = (lowerIndex + 0.5) / size;
          float upperY = (upperIndex + 0.5) / size;
          return mix(
            decodePacked(texture2D(u_front, vec2(0.5, lowerY))),
            decodePacked(texture2D(u_front, vec2(0.5, upperY))),
            blend
          );
        }

        float hash21(vec2 p){
          p = fract(p * vec2(123.34, 456.21));
          p += dot(p, p + 45.32);
          return fract(p.x * p.y);
        }

        float noise(vec2 p){
          vec2 i = floor(p);
          vec2 f = fract(p);
          f = f * f * (3.0 - 2.0 * f);
          float a = hash21(i);
          float b = hash21(i + vec2(1.0, 0.0));
          float c = hash21(i + vec2(0.0, 1.0));
          float d = hash21(i + vec2(1.0, 1.0));
          return mix(mix(a,b,f.x), mix(c,d,f.x), f.y);
        }

        float fbm(vec2 p){
          float value = 0.0;
          float amplitude = 0.5;
          for(int i=0;i<4;i++){
            value += noise(p) * amplitude;
            p = p * 2.03 + vec2(13.7, 9.2);
            amplitude *= 0.48;
          }
          return value;
        }

        void main(){
          // Match the top-left origin of the SVG reveal and wet-film paths.
      vec2 uv = vec2(v_uv.x, 1.0 - v_uv.y);
          float pixel = 1.0 / max(u_resolution.x, 1.0);
          bool horizontal = u_orientation > 0.5;
          float along = horizontal ? uv.x : uv.y;
          float across = horizontal ? uv.y : uv.x;
          float axisResolution = horizontal ? u_resolution.x : u_resolution.y;
          float front = decodeFront(along);
          float frontUp = decodeFront(along + 1.5 / max(axisResolution, 1.0));
          float frontDown = decodeFront(along - 1.5 / max(axisResolution, 1.0));
          float slope = (frontUp - frontDown) * 110.0;
          float signedDistance = (across - front) * u_direction;
          float absoluteDistance = abs(signedDistance);
          float micro = (fbm(uv * vec2(22.0, 14.0) + vec2(u_time * 0.17, -u_time * 0.11)) - 0.5);
          float ridgeWidth = mix(0.014, 0.028, u_intensity) + micro * 0.0032;
          float ridge = exp(-pow(absoluteDistance / max(0.002, ridgeWidth), 2.0));
          float innerRidge = exp(-pow((signedDistance + 0.004) / max(0.001, ridgeWidth * 0.34), 2.0));
          float trailing = smoothstep(-0.19, -0.02, signedDistance) * (1.0 - smoothstep(-0.005, 0.034, signedDistance));
          float coated = 1.0 - smoothstep(-0.03, 0.022, signedDistance);
          float wetTexture = fbm(uv * vec2(8.0, 10.0) + vec2(-u_time * 0.07, u_time * 0.045));
          float capillary = pow(max(0.0, sin((uv.y * 73.0 + uv.x * 19.0) + wetTexture * 5.0 - u_time * 1.8)), 11.0);
          vec3 normal = horizontal ? normalize(vec3(-slope * 0.18 + micro * 0.5, -u_direction, 2.8)) : normalize(vec3(-u_direction, -slope * 0.18 + micro * 0.5, 2.8));
          vec3 lightDirection = normalize(vec3(-0.48, -0.62, 1.25));
          float specular = pow(max(dot(normal, lightDirection), 0.0), 15.0);
          float filmAlpha = (trailing * (0.090 + wetTexture * 0.070) + coated * (0.030 + wetTexture * 0.028)) * u_intensity;
          float highlightAlpha = ridge * (0.30 + specular * 0.74) + innerRidge * 0.58 + capillary * trailing * 0.24;
          float shadowAlpha = ridge * smoothstep(0.001, 0.03, signedDistance) * 0.13;

          if(u_phase > 0.5){
            float drainLine = mix(-0.10, 1.10, u_drain) + (fbm(vec2(uv.x * 5.0, u_time * 0.08)) - 0.5) * 0.055 + (uv.x - 0.5) * 0.035;
            float drainDistance = uv.y - drainLine;
            float remaining = smoothstep(-0.025, 0.055, drainDistance);
            float drainRidge = exp(-pow(drainDistance / 0.028, 2.0));
            float residue = remaining * (1.0 - u_drain) * (0.040 + wetTexture * 0.032);
            float drainSpecular = drainRidge * (0.28 + specular * 0.46) * (1.0 - u_drain);
            vec3 drainColor = mix(vec3(1.0), u_accent, 0.16);
            float alpha = clamp(residue + drainSpecular, 0.0, 0.74) * 1.38;
            gl_FragColor = vec4(drainColor, alpha);
            return;
          }

          vec3 lightColor = mix(vec3(1.0), u_accent, 0.12 + wetTexture * 0.05);
          vec3 shadowColor = vec3(0.025, 0.024, 0.023);
          float lightAmount = clamp(filmAlpha + highlightAlpha, 0.0, 0.56);
          float darkAmount = clamp(shadowAlpha, 0.0, 0.11);
          vec3 color = mix(shadowColor, lightColor, lightAmount / max(0.0001, lightAmount + darkAmount));
          float alpha = clamp(lightAmount + darkAmount, 0.0, 0.84);
          if(alpha < 0.002) discard;
          gl_FragColor = vec4(color, alpha);
        }
      `);
      const program = gl.createProgram();
      gl.attachShader(program, vertex);
      gl.attachShader(program, fragment);
      gl.linkProgram(program);
      if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
        throw new Error(gl.getProgramInfoLog(program) || 'Program link failed');
      }
      gl.deleteShader(vertex);
      gl.deleteShader(fragment);
      this.program = program;
      gl.useProgram(program);

      const buffer = gl.createBuffer();
      gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
      gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1,-1, 1,-1, -1,1, -1,1, 1,-1, 1,1]), gl.STATIC_DRAW);
      const position = gl.getAttribLocation(program, 'a_position');
      gl.enableVertexAttribArray(position);
      gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0);

      this.uniforms = {
        front: gl.getUniformLocation(program, 'u_front'),
        resolution: gl.getUniformLocation(program, 'u_resolution'),
        time: gl.getUniformLocation(program, 'u_time'),
        direction: gl.getUniformLocation(program, 'u_direction'),
        phase: gl.getUniformLocation(program, 'u_phase'),
        drain: gl.getUniformLocation(program, 'u_drain'),
        intensity: gl.getUniformLocation(program, 'u_intensity'),
        orientation: gl.getUniformLocation(program, 'u_orientation'),
        frontSize: gl.getUniformLocation(program, 'u_frontSize'),
        accent: gl.getUniformLocation(program, 'u_accent')
      };

      this.frontTexture = gl.createTexture();
      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, this.frontTexture);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      gl.uniform1i(this.uniforms.front, 0);
      gl.enable(gl.BLEND);
      gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
      gl.clearColor(0, 0, 0, 0);
    }

    resize(width, height) {
      const memoryLimit = deviceMemory() <= 2 ? 1.1 : (window.innerWidth < 750 ? 1.35 : 1.7);
      this.dpr = Math.min(window.devicePixelRatio || 1, memoryLimit);
      this.width = Math.max(1, Math.round(width * this.dpr));
      this.height = Math.max(1, Math.round(height * this.dpr));
      if (this.canvas.width !== this.width || this.canvas.height !== this.height) {
        this.canvas.width = this.width;
        this.canvas.height = this.height;
        this.gl.viewport(0, 0, this.width, this.height);
      }
      this.canvas.style.width = `${width}px`;
      this.canvas.style.height = `${height}px`;
    }

    uploadFront(samples) {
      const gl = this.gl;
      const length = samples.length;
      if (!this.frontData || this.frontData.length !== length * 4) {
        this.frontData = new Uint8Array(length * 4);
      }
      for (let index = 0; index < length; index += 1) {
        const normalized = clamp((samples[index] + 0.25) / 1.5);
        const packed = Math.round(normalized * 65535);
        this.frontData[index * 4] = (packed >> 8) & 255;
        this.frontData[index * 4 + 1] = packed & 255;
        this.frontData[index * 4 + 2] = 0;
        this.frontData[index * 4 + 3] = 255;
      }
      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, this.frontTexture);
      if (this.frontTextureLength !== length) {
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, 1, length, 0, gl.RGBA, gl.UNSIGNED_BYTE, this.frontData);
        this.frontTextureLength = length;
      } else {
        gl.texSubImage2D(gl.TEXTURE_2D, 0, 0, 0, 1, length, gl.RGBA, gl.UNSIGNED_BYTE, this.frontData);
      }
    }

    render(state) {
      const gl = this.gl;
      this.uploadFront(state.samples);
      gl.useProgram(this.program);
      gl.clear(gl.COLOR_BUFFER_BIT);
      gl.uniform2f(this.uniforms.resolution, this.width, this.height);
      gl.uniform1f(this.uniforms.time, state.elapsedSeconds);
      gl.uniform1f(this.uniforms.direction, state.direction);
      gl.uniform1f(this.uniforms.phase, state.phase === 'drain' ? 1 : 0);
      gl.uniform1f(this.uniforms.drain, state.drain || 0);
      gl.uniform1f(this.uniforms.intensity, state.intensity);
      gl.uniform1f(this.uniforms.orientation, state.orientation === 'horizontal' ? 1 : 0);
      gl.uniform1f(this.uniforms.frontSize, state.samples.length);
      gl.uniform3fv(this.uniforms.accent, accentRGB(state.accent));
      gl.drawArrays(gl.TRIANGLES, 0, 6);
    }

    clear() {
      this.gl.clear(this.gl.COLOR_BUFFER_BIT);
    }
  }

  function createWaterRenderer(canvas) {
    if (!canvas) return { renderer: null, canvas: null };
    if (!saveData() && deviceMemory() > 1) {
      try {
        return { renderer: new WebGLWater(canvas), canvas };
      } catch (error) {
        console.info('[Corsaille Living Water] WebGL enhancement unavailable; using optical fallback.', error?.message || error);
        // A canvas that has already requested WebGL cannot subsequently obtain a 2D context.
        // Replace it with an identical element before creating the fallback renderer.
        const replacement = canvas.cloneNode(false);
        canvas.replaceWith(replacement);
        canvas = replacement;
      }
    }
    return { renderer: new Canvas2DWater(canvas), canvas };
  }

  class LivingWaterTransition {
    constructor(root, options = {}) {
      this.root = root;
      this.viewport = root.querySelector(SELECTORS.viewport);
      this.canvas = root.querySelector(SELECTORS.canvas);
      this.film = root.querySelector(SELECTORS.film);
      this.refractStack = root.querySelector(SELECTORS.refract);
      this.revealPath = root.querySelector(SELECTORS.revealPath);
      this.oldBandPath = root.querySelector(SELECTORS.oldBandPath);
      this.newBandPath = root.querySelector(SELECTORS.newBandPath);
      this.wetPath = root.querySelector(SELECTORS.wetPath);
      this.revealClipId = root.dataset.cwlRevealClip;
      this.oldBandClipId = root.dataset.cwlOldBandClip;
      this.newBandClipId = root.dataset.cwlNewBandClip;
      this.wetClipId = root.dataset.cwlWetClip;
      this.duration = clamp(Number(root.dataset.cwlDuration) || 1560, 1050, 2400);
      this.intensity = clamp(Number(root.dataset.cwlIntensity) || 1, 0.72, 1.55);
      this.orientationSetting = root.dataset.cwlOrientation || 'adaptive';
      this.crossingRatio = 0.56;
      this.simpleFallback = useSimpleTransition();
      const rendererBundle = this.simpleFallback
        ? { renderer: null, canvas: this.canvas }
        : createWaterRenderer(this.canvas);
      this.renderer = rendererBundle.renderer;
      this.canvas = rendererBundle.canvas;
      const quality = root.dataset.cwlQuality || 'auto';
      const lowerQuality = quality === 'balanced' || saveData() || deviceMemory() <= 2;
      this.sampleCount = lowerQuality ? 74 : (window.innerWidth < 750 ? 96 : 132);
      this.front = this.simpleFallback ? null : new FluidFront(this.sampleCount, this.intensity);
      this.frame = 0;
      this.resizeObserver = null;
      this.active = false;
      this.simpleTimer = 0;
      this.simpleResolve = null;
      this.simpleSlides = [];
      this.clones = [];
      this.options = options;
      if (!this.simpleFallback) this.resize();
      if (!this.simpleFallback && 'ResizeObserver' in window && this.viewport) {
        this.resizeObserver = new ResizeObserver(() => this.resize());
        this.resizeObserver.observe(this.viewport);
      }
    }

    currentOrientation() {
      const setting = (this.orientationSetting || 'vertical').toLowerCase();
      if (setting === 'horizontal') return 'horizontal';
      if (setting === 'adaptive') return window.innerWidth < 750 ? 'horizontal' : 'vertical';
      return 'vertical';
    }

    resize() {
      if (!this.viewport || !this.renderer) return;
      const bounds = this.viewport.getBoundingClientRect();
      this.renderer.resize(bounds.width, bounds.height);
    }

    applyClip(element, clipId) {
      if (!element || !clipId) return;
      const value = `url(#${clipId})`;
      element.style.clipPath = value;
      element.style.webkitClipPath = value;
    }

    setPath(pathElement, value) {
      pathElement?.setAttribute('d', value);
    }

    createRefractionClones(_outgoing, incoming) {
      if (!this.refractStack) return;
      this.refractStack.replaceChildren();
      const newClone = stripCloneAccessibility(incoming.cloneNode(true));
      newClone.classList.add('cwl-slide--refract', 'cwl-slide--refract-new');
      newClone.classList.remove('is-active', 'is-incoming', 'is-outgoing');
      this.applyClip(newClone, this.newBandClipId);
      // Exactly one refractive face: the incoming creation at the same leading
      // edge as the reveal. An outgoing clone is what caused the doubled,
      // opposing desktop sweep in the rejected renderer.
      this.refractStack.append(newClone);
      this.clones = [newClone];
    }

    cleanup(outgoing, incoming) {
      cancelAnimationFrame(this.frame);
      this.frame = 0;
      clearTimeout(this.simpleTimer);
      this.simpleTimer = 0;
      const interruptedResolve = this.simpleResolve;
      this.simpleResolve = null;
      if (interruptedResolve) interruptedResolve(false);
      this.active = false;
      this.root.classList.remove('is-water-transitioning', 'is-water-draining', 'is-simple-transitioning');
      const simpleSlides = new Set([outgoing, incoming, ...this.simpleSlides].filter(Boolean));
      simpleSlides.forEach((slide) => {
        slide.style.removeProperty('opacity');
        slide.style.removeProperty('transition');
        slide.style.removeProperty('visibility');
      });
      this.simpleSlides = [];
      outgoing?.classList.remove('is-outgoing');
      incoming?.classList.remove('is-incoming');
      if (incoming) {
        incoming.style.removeProperty('clip-path');
        incoming.style.removeProperty('-webkit-clip-path');
        incoming.style.removeProperty('visibility');
      }
      this.refractStack?.replaceChildren();
      this.clones = [];
      if (this.film) {
        this.film.style.opacity = '0';
        this.film.style.removeProperty('clip-path');
        this.film.style.removeProperty('-webkit-clip-path');
        this.film.style.removeProperty('transform');
      }
      this.renderer?.clear();
      if (this.canvas) this.canvas.style.opacity = '0';
      this.setPath(this.revealPath, fullPath());
      this.setPath(this.oldBandPath, emptyPath());
      this.setPath(this.newBandPath, emptyPath());
      this.setPath(this.wetPath, emptyPath());
    }

    previewFrame(outgoing, incoming, { progress = 0.5, direction = 1, accent = 'crystal', seed = 31.7, phase = 'cross', drain = 0, orientation = this.currentOrientation() } = {}) {
      if (!outgoing || !incoming || outgoing === incoming) return false;
      if (this.simpleFallback || !this.front) return false;
      this.resize();
      this.root.classList.add('is-water-transitioning');
      this.root.classList.toggle('is-water-draining', phase === 'drain');
      outgoing.classList.add('is-outgoing');
      incoming.classList.add('is-incoming');
      incoming.style.visibility = 'visible';
      this.applyClip(incoming, this.revealClipId);
      if (!this.clones.length) this.createRefractionClones(outgoing, incoming);
      this.applyClip(this.film, this.wetClipId);
      if (this.canvas) this.canvas.style.opacity = '1';
      if (this.film) this.film.style.opacity = phase === 'drain' ? String((1 - drain) * .56 + .04) : '.58';
      const samples = this.front.generate(clamp(progress), 0.73, direction, seed);
      const ridgeWidth = (orientation === 'horizontal' ? (window.innerWidth < 750 ? 0.024 : 0.016) : (window.innerWidth < 750 ? 0.048 : 0.022)) * this.intensity;
      const refractionWidth = (orientation === 'horizontal' ? (window.innerWidth < 750 ? 0.036 : 0.028) : (window.innerWidth < 750 ? 0.072 : 0.034)) * this.intensity;
      if (phase === 'drain') {
        this.setPath(this.revealPath, fullPath());
        this.setPath(this.oldBandPath, emptyPath());
        this.setPath(this.newBandPath, emptyPath());
        this.setPath(this.wetPath, drainagePath(drain, .73, seed));
        this.clones.forEach((clone) => { clone.style.opacity = '0'; });
      } else {
        this.setPath(this.revealPath, revealPath(samples, direction, 0, orientation));
        const behind = direction > 0 ? -refractionWidth : refractionWidth;
        this.setPath(this.oldBandPath, emptyPath());
        this.setPath(this.newBandPath, bandPath(samples, behind, 0, orientation));
        const nose = direction > 0 ? ridgeWidth : -ridgeWidth;
        this.setPath(this.wetPath, revealPath(samples, direction, nose, orientation));
        if (this.clones[0]) { this.clones[0].style.transform = `translate3d(${-direction * 4.8}px,-1.8px,0) scale(1.0105)`; this.clones[0].style.opacity = '.98'; }
      }
      this.renderer?.render({ samples, direction, phase, drain, accent, intensity: this.intensity, elapsedSeconds: .73, orientation });
      return true;
    }

    async runSimpleCrossfade(outgoing, incoming) {
      if (this.active) return false;
      this.active = true;
      this.simpleSlides = [outgoing, incoming];
      const duration = 360;
      this.root.classList.add('is-simple-transitioning');
      outgoing.classList.add('is-outgoing');
      incoming.classList.add('is-incoming');
      incoming.style.visibility = 'visible';
      incoming.style.opacity = '0';
      outgoing.style.opacity = '1';
      incoming.style.transition = `opacity ${duration}ms cubic-bezier(.22,.61,.36,1)`;
      outgoing.style.transition = `opacity ${duration}ms cubic-bezier(.22,.61,.36,1)`;

      // Commit the starting frame before fading. This branch intentionally
      // avoids WebGL, SVG clip updates, backdrop-film rasterization and clones.
      void incoming.offsetWidth;
      incoming.style.opacity = '1';
      outgoing.style.opacity = '0';
      const completed = await new Promise((resolve) => {
        this.simpleResolve = resolve;
        this.simpleTimer = window.setTimeout(() => {
          this.simpleTimer = 0;
          this.simpleResolve = null;
          resolve(true);
        }, duration + 40);
      });
      if (!completed) return false;

      const connected = this.root.isConnected;
      this.cleanup(outgoing, incoming);
      return connected;
    }

    async run(outgoing, incoming, { direction = 1, accent = 'crystal', seed = Math.random() * 100, orientation = this.currentOrientation() } = {}) {
      if (!outgoing || !incoming || outgoing === incoming) return false;
      if (this.active) return false;
      await decodeSlide(incoming, { eager: true });
      if (!this.root.isConnected) return false;

      if (reducedMotion()) {
        outgoing.classList.remove('is-active');
        incoming.classList.add('is-active');
        return true;
      }

      if (this.simpleFallback) return this.runSimpleCrossfade(outgoing, incoming);

      this.active = true;
      this.resize();
      this.root.classList.add('is-water-transitioning');
      outgoing.classList.add('is-outgoing');
      incoming.classList.add('is-incoming');
      incoming.style.visibility = 'visible';
      this.applyClip(incoming, this.revealClipId);
      this.createRefractionClones(outgoing, incoming);
      this.applyClip(this.film, this.wetClipId);
      if (this.canvas) this.canvas.style.opacity = '1';
      if (this.film) this.film.style.opacity = '1';

      const started = performance.now();
      const crossDuration = this.duration * this.crossingRatio;
      const drainDuration = this.duration - crossDuration;
      let resolved = false;

      return new Promise((resolve) => {
        const finish = (success = true) => {
          if (resolved) return;
          resolved = true;
          this.cleanup(outgoing, incoming);
          resolve(success);
        };

        const tick = (now) => {
          if (!this.root.isConnected) {
            finish(false);
            return;
          }
          const elapsed = now - started;
          const elapsedSeconds = elapsed / 1000;
          const inCrossing = elapsed <= crossDuration;
          const crossT = clamp(elapsed / crossDuration);
          const drainT = clamp((elapsed - crossDuration) / Math.max(1, drainDuration));
          const progress = inCrossing ? fluidEase(crossT) : 1;
          const samples = this.front.generate(progress, elapsedSeconds, direction, seed);
          const ridgeWidth = (orientation === 'horizontal' ? (window.innerWidth < 750 ? 0.024 : 0.016) : (window.innerWidth < 750 ? 0.048 : 0.022)) * this.intensity;
          const refractionWidth = (orientation === 'horizontal' ? (window.innerWidth < 750 ? 0.036 : 0.028) : (window.innerWidth < 750 ? 0.072 : 0.034)) * this.intensity;

          if (inCrossing) {
            this.setPath(this.revealPath, revealPath(samples, direction, 0, orientation));
            const behind = direction > 0 ? -refractionWidth : refractionWidth;
            this.setPath(this.oldBandPath, emptyPath());
            this.setPath(this.newBandPath, bandPath(samples, behind, 0, orientation));
            const nose = direction > 0 ? ridgeWidth : -ridgeWidth;
            // The transparent film coats every part already crossed by the water.
            // This makes the surface become wet, rather than showing only a moving stripe.
            this.setPath(this.wetPath, revealPath(samples, direction, nose, orientation));
            this.root.classList.remove('is-water-draining');
            if (this.film) {
              this.film.style.opacity = String(0.50 + Math.sin(Math.PI * crossT) * 0.18);
              this.film.style.transform = `translate3d(${direction * Math.sin(crossT * Math.PI) * 1.6}px,${Math.sin(crossT * Math.PI) * 0.85}px,0)`;
            }
            if (this.clones[0]) {
              this.clones[0].style.transform = `translate3d(${-direction * (4.0 + Math.sin(crossT * Math.PI) * 4.2)}px,${-1.6 - Math.sin(crossT * Math.PI) * 1.5}px,0) scale(1.0105)`;
              this.clones[0].style.opacity = String(0.88 + Math.sin(Math.PI * crossT) * 0.16);
            }
            this.renderer?.render({ samples, direction, phase: 'cross', drain: 0, accent, intensity: this.intensity, elapsedSeconds, orientation });
          } else {
            this.root.classList.add('is-water-draining');
            this.setPath(this.revealPath, fullPath());
            this.setPath(this.oldBandPath, emptyPath());
            this.setPath(this.newBandPath, emptyPath());
            this.setPath(this.wetPath, drainagePath(drainT, elapsedSeconds, seed));
            if (this.film) {
              this.film.style.opacity = String((1 - smootherstep(drainT)) * 0.54 + 0.06);
              this.film.style.transform = `translate3d(${direction * drainT * 3.2}px,${drainT * 8.0}px,0)`;
            }
            this.clones.forEach((clone) => { clone.style.opacity = '0'; });
            this.renderer?.render({ samples, direction, phase: 'drain', drain: drainT, accent, intensity: this.intensity, elapsedSeconds, orientation });
          }

          if (elapsed < this.duration) {
            this.frame = requestAnimationFrame(tick);
          } else {
            finish(true);
          }
        };

        this.frame = requestAnimationFrame(tick);
        window.setTimeout(() => finish(false), this.duration + 900);
      });
    }

    destroy() {
      this.cleanup(this.simpleSlides[0], this.simpleSlides[1]);
      this.resizeObserver?.disconnect();
    }
  }

  class SignatureStageController {
    constructor(root) {
      this.root = root;
      this.slides = [...root.querySelectorAll(SELECTORS.slide)];
      this.viewport = root.querySelector(SELECTORS.viewport);
      this.dots = [...root.querySelectorAll(SELECTORS.dot)];
      this.previousButton = root.querySelector(SELECTORS.previous);
      this.nextButton = root.querySelector(SELECTORS.next);
      this.live = root.querySelector(SELECTORS.live);
      this.activeIndex = Math.max(0, this.slides.findIndex((slide) => slide.classList.contains('is-active')));
      this.autoplay = root.dataset.cwlAutoplay !== 'false';
      this.fallbackInterval = clamp(Number(root.dataset.cwlInterval) || 6000, 2500, 30000);
      this.directionMode = root.dataset.cwlDirection || 'alternate';
      this.explicitlyPaused = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches || false;
      this.inView = false;
      this.initialExposurePending = true;
      this.autoplayTimer = 0;
      this.holdDuration = 0;
      this.remainingHold = 0;
      this.holdStartedAt = 0;
      this.holdDeadline = 0;
      this.transitioning = false;
      this.pendingIndex = null;
      this.transitionSerial = 0;
      this.abortController = new AbortController();
      this.transition = new LivingWaterTransition(root);
      this.pointerStart = null;
      this.suppressClickUntil = -Infinity;
      this.lastTouchPointerAt = -Infinity;
      this.initialize();
    }

    initialize() {
      if (this.slides.length === 0) return;
      this.sync();
      this.bind();
      // Build the first slide clock in a paused state. It starts only when a
      // meaningful part of the stage is visible, so scrolling never burns the
      // first exposure off-screen.
      this.startHold(this.currentHoldDuration(), false);
      this.observe();
    }

    currentHoldDuration() {
      const slide = this.slides[this.activeIndex];
      const duration = clamp(Number(slide?.dataset.cwlHold) || this.fallbackInterval, 2500, 30000);
      const family = this.root.closest('[data-cs-component="feature-stage"]')?.dataset.csObjectType;
      const shorterHold = (family === 'engagement-ring' || family === 'wedding-band') && (this.activeIndex === 1 || this.activeIndex === 2);
      return duration - (shorterHold ? 500 : 0);
    }

    bind() {
      const { signal } = this.abortController;
      this.previousButton?.addEventListener('click', () => this.goBy(-1, true), { signal });
      this.nextButton?.addEventListener('click', () => this.goBy(1, true), { signal });
      this.dots.forEach((dot) => dot.addEventListener('click', () => this.goTo(Number(dot.dataset.cwlDot), true), { signal }));
      // Hover and focused controls never suspend the automatic presentation.
      const clearPointer = (event, suppressClick = false) => {
        const pointer = this.pointerStart;
        if (!pointer || (event?.pointerId != null && event.pointerId !== pointer.id)) return;
        this.pointerStart = null;
        if (suppressClick || pointer.moved) this.suppressClickUntil = performance.now() + 460;
        this.root.classList.remove('is-dragging');
        this.root.style.removeProperty('--cwl-drag-x');
        try {
          if (this.viewport?.hasPointerCapture?.(pointer.id)) this.viewport.releasePointerCapture(pointer.id);
        } catch (_) {
          // Capture may already have been released by a browser gesture or DOM change.
        }
      };

      this.viewport?.addEventListener('pointerdown', (event) => {
        if (!event.isPrimary || (event.pointerType === 'mouse' && event.button !== 0)) return;
        if (event.pointerType !== 'mouse') this.lastTouchPointerAt = performance.now();
        this.pointerStart = {
          x: event.clientX,
          y: event.clientY,
          id: event.pointerId,
          axis: null,
          moved: false,
          captured: false
        };
      }, { signal, passive: true });
      this.viewport?.addEventListener('pointermove', (event) => {
        const pointer = this.pointerStart;
        if (!pointer || event.pointerId !== pointer.id) return;
        const dx = event.clientX - pointer.x;
        const dy = event.clientY - pointer.y;
        const absoluteX = Math.abs(dx);
        const absoluteY = Math.abs(dy);

        if (!pointer.axis && Math.max(absoluteX, absoluteY) > 8) {
          if (absoluteX > absoluteY * 1.18) pointer.axis = 'x';
          else if (absoluteY > absoluteX * 1.18) pointer.axis = 'y';
        }
        if (pointer.axis !== 'x') return;

        pointer.moved = true;
        if (!pointer.captured) {
          try {
            this.viewport?.setPointerCapture?.(event.pointerId);
            pointer.captured = Boolean(this.viewport?.hasPointerCapture?.(event.pointerId));
          } catch (_) {
            pointer.captured = false;
          }
          this.root.classList.add('is-dragging');
        }
        this.root.style.setProperty('--cwl-drag-x', `${Math.max(-22, Math.min(22, dx * .08)).toFixed(1)}px`);
        if (event.cancelable) event.preventDefault();
      }, { signal, passive: false });
      this.viewport?.addEventListener('pointerup', (event) => {
        const pointer = this.pointerStart;
        if (!pointer || event.pointerId !== pointer.id) return;
        const dx = event.clientX - pointer.x;
        const dy = event.clientY - pointer.y;
        const committed = pointer.axis === 'x' && Math.abs(dx) > 44 && Math.abs(dx) > Math.abs(dy) * 1.25;
        clearPointer(event, pointer.axis === 'x' && pointer.moved);
        // A vertical page scroll is deliberately ignored. Only an unambiguous
        // horizontal gesture changes product.
        if (committed) this.goBy(dx < 0 ? 1 : -1, true);
      }, { signal });
      this.viewport?.addEventListener('pointercancel', (event) => {
        const pointer = this.pointerStart;
        clearPointer(event, pointer?.axis === 'x' && pointer.moved);
      }, { signal });
      this.viewport?.addEventListener('lostpointercapture', (event) => {
        const pointer = this.pointerStart;
        clearPointer(event, pointer?.axis === 'x' && pointer.moved);
      }, { signal });
      this.viewport?.addEventListener('pointerleave', (event) => {
        if (this.pointerStart && !this.pointerStart.captured) clearPointer(event, false);
      }, { signal });
      this.viewport?.addEventListener('dragstart', (event) => event.preventDefault(), { signal });
      this.viewport?.addEventListener('click', (event) => {
        if (performance.now() >= this.suppressClickUntil) return;
        event.preventDefault();
        event.stopImmediatePropagation();
      }, { signal, capture: true });
      this.viewport?.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') { event.preventDefault(); this.goBy(-1, true); }
        if (event.key === 'ArrowRight') { event.preventDefault(); this.goBy(1, true); }
      }, { signal });

      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.pauseClock();
        else this.resumeClock();
        this.syncAutoplayState();
      }, { signal });
    }

    observe() {
      if (!('IntersectionObserver' in window)) {
        this.inView = true;
        this.root.classList.add('is-in-view');
        this.resumeClock();
        return;
      }
      this.observer = new IntersectionObserver((entries) => {
        for (const entry of entries) {
          const nextInView = entry.isIntersecting && entry.intersectionRatio >= 0.10;
          if (nextInView === this.inView) continue;
          this.inView = nextInView;
          this.root.classList.toggle('is-in-view', this.inView);
          if (this.inView) {
            decodeSlide(this.slides[normalizeIndex(this.activeIndex + 1, this.slides.length)]);
            this.resumeClock();
          }
          else this.pauseClock();
          this.syncAutoplayState();
        }
      }, { threshold: [0, 0.10, 0.62] });
      this.observer.observe(this.root);
    }

    canAutoplay() {
      return this.autoplay
        && this.slides.length > 1
        && this.inView
        && !this.explicitlyPaused
        && !this.transitioning
        && !document.hidden;
    }

    syncAutoplayState() {
      const running = this.canAutoplay() && Boolean(this.autoplayTimer);
      this.root.classList.toggle('is-autoplaying', running);
      this.root.classList.toggle('is-paused', !running);
      this.root.classList.toggle('is-clock-paused', this.root.classList.contains('is-holding') && !running);
    }

    clearAutoplayTimer() {
      clearTimeout(this.autoplayTimer);
      this.autoplayTimer = 0;
    }

    resetProgress(duration) {
      this.root.style.setProperty('--cwl-active-hold', `${duration}ms`);
      this.root.style.setProperty('--cwl-interval', `${duration}ms`);
      this.root.classList.remove('is-holding');
      // Force only the one-pixel progress rail to restart. No card, image or
      // water layer is reconstructed.
      void this.root.offsetWidth;
      this.root.classList.add('is-holding');
    }

    startHold(duration = this.currentHoldDuration(), shouldResume = true) {
      if (!this.autoplay) {
        this.stopHold();
        return;
      }
      this.clearAutoplayTimer();
      this.holdDuration = clamp(Number(duration) || this.currentHoldDuration(), 1800, 30000);
      this.remainingHold = this.holdDuration;
      this.holdStartedAt = 0;
      this.holdDeadline = 0;
      this.resetProgress(this.holdDuration);
      if (shouldResume) this.resumeClock();
      else this.syncAutoplayState();
    }

    pauseClock() {
      if (this.autoplayTimer && this.holdDeadline) {
        this.remainingHold = Math.max(80, this.holdDeadline - performance.now());
      }
      this.clearAutoplayTimer();
      this.holdStartedAt = 0;
      this.holdDeadline = 0;
      this.syncAutoplayState();
    }

    resumeClock() {
      // Measure the first exposure from the first 10% visibility crossing.
      if (this.initialExposurePending && this.inView && !this.transitioning) {
        this.initialExposurePending = false;
        this.startHold(window.innerWidth < 750 ? 1800 : 2500, false);
      }
      if (!this.root.classList.contains('is-holding') && !this.transitioning) {
        this.startHold(this.currentHoldDuration(), false);
      }
      this.clearAutoplayTimer();
      if (!this.canAutoplay()) { this.syncAutoplayState(); return; }
      const delay = clamp(this.remainingHold || this.holdDuration || this.currentHoldDuration(), 80, 30000);
      this.remainingHold = delay;
      this.holdStartedAt = performance.now();
      this.holdDeadline = this.holdStartedAt + delay;
      this.autoplayTimer = window.setTimeout(() => {
        this.autoplayTimer = 0;
        this.remainingHold = 0;
        this.holdStartedAt = 0;
        this.holdDeadline = 0;
        this.goBy(1, false);
      }, delay);
      this.syncAutoplayState();
    }

    stopHold() {
      this.clearAutoplayTimer();
      this.holdDuration = 0;
      this.remainingHold = 0;
      this.holdStartedAt = 0;
      this.holdDeadline = 0;
      this.root.classList.remove('is-holding', 'is-clock-paused');
      this.syncAutoplayState();
    }

    // Compatibility aliases retained for Shopify preview tooling and older QA.
    clearTimer() { this.pauseClock(); }
    schedule(delay) {
      if (Number.isFinite(delay)) this.startHold(delay, true);
      else this.resumeClock();
    }

    directionFor(from, to) {
      if (this.directionMode === 'left') return 1;
      if (this.directionMode === 'right') return -1;
      if (this.directionMode === 'navigation') return to >= from ? 1 : -1;
      return this.transitionSerial % 2 === 0 ? 1 : -1;
    }

    sync(announce = false) {
      this.slides.forEach((slide, index) => {
        const active = index === this.activeIndex;
        slide.classList.toggle('is-active', active);
        if (!this.transitioning || active) setSlideInteractive(slide, active);
      });
      this.dots.forEach((dot, index) => {
        const active = index === this.activeIndex;
        dot.classList.toggle('is-active', active);
        active ? dot.setAttribute('aria-current', 'true') : dot.removeAttribute('aria-current');
      });
      const active = this.slides[this.activeIndex];
      if (this.live && active && announce) this.live.textContent = active.dataset.cwlTitle || '';
      this.syncAutoplayState();
    }

    goBy(delta, manual) {
      this.goTo(normalizeIndex(this.activeIndex + delta, this.slides.length), manual);
    }

    async goTo(index, manual = false) {
      const targetIndex = normalizeIndex(index, this.slides.length);
      // Manual choices restart the normal dwell; autoplay continues through every slide.
      if (targetIndex === this.activeIndex || this.slides.length < 2) {
        if (manual && this.slides.length > 1) this.startHold(this.currentHoldDuration(), true);
        return;
      }
      if (this.transitioning) {
        this.pendingIndex = targetIndex;
        return;
      }

      this.stopHold();
      const outgoing = this.slides[this.activeIndex];
      const incoming = this.slides[targetIndex];
      this.transitioning = true;
      this.transitionSerial += 1;
      this.root.classList.add('is-changing');
      incoming.style.visibility = 'visible';
      setSlideInteractive(incoming, false);
      const orientation = this.transition.currentOrientation();
      const direction = orientation === 'horizontal' ? 1 : this.directionFor(this.activeIndex, targetIndex);
      const accent = incoming.dataset.cwlAccent || 'crystal';
      const seed = (targetIndex + 1) * 13.731 + this.transitionSerial * 3.417;

      const success = await this.transition.run(outgoing, incoming, { direction, accent, seed, orientation });
      if (success && this.root.isConnected) {
        outgoing.classList.remove('is-active');
        incoming.classList.add('is-active');
        this.activeIndex = targetIndex;
      }
      this.transitioning = false;
      this.root.classList.remove('is-changing');
      this.sync(manual);
      decodeSlide(this.slides[normalizeIndex(this.activeIndex + 1, this.slides.length)]);

      if (this.pendingIndex !== null && this.pendingIndex !== this.activeIndex) {
        const pending = this.pendingIndex;
        this.pendingIndex = null;
        window.setTimeout(() => this.goTo(pending, true), 110);
      } else {
        this.pendingIndex = null;
        this.startHold(this.currentHoldDuration(), true);
      }
    }

    selectBlock(blockId) {
      const index = this.slides.findIndex((slide) => slide.dataset.shopifyBlockId === blockId);
      if (index >= 0) {
        this.explicitlyPaused = true;
        this.goTo(index, true);
      }
    }

    destroy() {
      this.stopHold();
      this.observer?.disconnect();
      this.abortController.abort();
      this.transition.destroy();
      delete this.root.cwlController;
      delete this.root.dataset.cwlReady;
    }
  }

  class HeroController {
    constructor(root) {
      this.root = root;
      this.slides = [...root.querySelectorAll(SELECTORS.slide)];
      this.dots = [...root.querySelectorAll(SELECTORS.dot)];
      this.activeIndex = Math.max(0, this.slides.findIndex((slide) => slide.classList.contains('is-active')));
      this.autoplay = root.dataset.cwlAutoplay !== 'false';
      this.interval = clamp(Number(root.dataset.cwlInterval) || 6000, 5200, 12000);
      this.mode = root.dataset.cwlHeroTransition || 'veil';
      this.explicitlyPaused = reducedMotion();
      this.inView = false;
      this.transitioning = false;
      this.timer = 0;
      this.abortController = new AbortController();
      this.water = this.mode === 'water' ? new LivingWaterTransition(root) : null;
      this.initialize();
    }

    initialize() {
      if (!this.slides.length) return;
      this.root.style.setProperty('--cwl-hero-interval', `${this.interval}ms`);
      this.sync();
      const { signal } = this.abortController;
      this.dots.forEach((dot) => dot.addEventListener('click', () => this.goTo(Number(dot.dataset.cwlDot), true), { signal }));
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver((entries) => {
          for (const entry of entries) {
            this.inView = entry.isIntersecting && entry.intersectionRatio >= 0.2;
            this.root.classList.toggle('is-in-view', this.inView);
            this.inView ? this.schedule() : this.clearTimer();
          }
        }, { threshold: [0, 0.2, 0.55] });
        this.observer.observe(this.root);
      } else {
        this.inView = true;
        this.schedule();
      }
      document.addEventListener('visibilitychange', () => document.hidden ? this.clearTimer() : this.schedule(), { signal });
      decodeSlide(this.slides[normalizeIndex(this.activeIndex + 1, this.slides.length)]);
    }

    canAutoplay() {
      return this.autoplay && this.slides.length > 1 && this.inView && !this.explicitlyPaused && !this.transitioning && !document.hidden;
    }

    clearTimer() { clearTimeout(this.timer); this.timer = 0; }
    schedule() {
      this.clearTimer();
      if (!this.canAutoplay()) { this.syncAutoplayState(); return; }
      this.timer = window.setTimeout(() => this.goTo(this.activeIndex + 1, false), this.interval);
      this.syncAutoplayState();
    }
    syncAutoplayState() {
      this.root.classList.toggle('is-autoplaying', this.canAutoplay());
      this.root.classList.toggle('is-paused', !this.canAutoplay());
    }
    sync() {
      this.slides.forEach((slide, index) => {
        const active = index === this.activeIndex;
        slide.classList.toggle('is-active', active);
        setSlideInteractive(slide, active);
      });
      this.dots.forEach((dot, index) => {
        const active = index === this.activeIndex;
        dot.classList.toggle('is-active', active);
        active ? dot.setAttribute('aria-current', 'true') : dot.removeAttribute('aria-current');
      });
      this.syncAutoplayState();
    }

    async veilTransition(outgoing, incoming) {
      await decodeSlide(incoming, { eager: true });
      if (reducedMotion()) return true;
      incoming.style.opacity = '0';
      outgoing.style.opacity = '1';
      incoming.classList.add('is-incoming');
      outgoing.classList.add('is-outgoing');
      this.root.classList.add('is-veil-transitioning');
      const duration = 1480;
      const started = performance.now();
      return new Promise((resolve) => {
        const tick = (now) => {
          const t = clamp((now - started) / duration);
          incoming.style.opacity = String(smoothstep(0.20, 0.90, t));
          outgoing.style.opacity = String(1 - smoothstep(0.08, 0.86, t));
          if (t < 1) requestAnimationFrame(tick);
          else {
            incoming.style.removeProperty('opacity');
            outgoing.style.removeProperty('opacity');
            incoming.classList.remove('is-incoming');
            outgoing.classList.remove('is-outgoing');
            this.root.classList.remove('is-veil-transitioning');
            resolve(true);
          }
        };
        requestAnimationFrame(tick);
      });
    }

    async goTo(index, manual = false) {
      const target = normalizeIndex(index, this.slides.length);
      if (target === this.activeIndex || this.transitioning || this.slides.length < 2) {
        if (manual && this.slides.length > 1) this.schedule();
        return;
      }
      // A dot selection restarts the clock after settling; it never becomes a
      // hidden permanent pause.
      this.clearTimer();
      this.transitioning = true;
      const outgoing = this.slides[this.activeIndex];
      const incoming = this.slides[target];
      incoming.style.visibility = 'visible';
      let success;
      if (this.mode === 'water' && this.water) {
        const orientation = this.water.currentOrientation();
        success = await this.water.run(outgoing, incoming, { direction: orientation === 'horizontal' ? 1 : (target > this.activeIndex ? 1 : -1), accent: incoming.dataset.cwlAccent || 'crystal', seed: target * 11.37 + 2.4, orientation });
      } else {
        success = await this.veilTransition(outgoing, incoming);
      }
      if (success && this.root.isConnected) {
        outgoing.classList.remove('is-active');
        incoming.classList.add('is-active');
        this.activeIndex = target;
      }
      this.transitioning = false;
      this.sync();
      decodeSlide(this.slides[normalizeIndex(this.activeIndex + 1, this.slides.length)]);
      this.schedule();
    }

    selectBlock(blockId) {
      const index = this.slides.findIndex((slide) => slide.dataset.shopifyBlockId === blockId);
      if (index >= 0) {
        this.explicitlyPaused = true;
        this.goTo(index, true);
      }
    }

    destroy() {
      this.clearTimer();
      this.observer?.disconnect();
      this.abortController.abort();
      this.water?.destroy();
      delete this.root.cwlController;
      delete this.root.dataset.cwlReady;
    }
  }

  function initializeRoot(root, Controller) {
    if (!root || root.dataset.cwlReady === 'true') return;
    root.dataset.cwlReady = 'true';
    root.cwlController = new Controller(root);
  }

  function initialize(scope = document) {
    scope.querySelectorAll?.(SELECTORS.stage).forEach((root) => initializeRoot(root, SignatureStageController));
    scope.querySelectorAll?.(SELECTORS.hero).forEach((root) => initializeRoot(root, HeroController));
  }

  function destroyScope(scope) {
    scope.querySelectorAll?.(`${SELECTORS.stage},${SELECTORS.hero}`).forEach((root) => root.cwlController?.destroy());
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize());
  else initialize();

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroyScope(event.target));
  document.addEventListener('shopify:block:select', (event) => {
    const root = event.target.closest?.(`${SELECTORS.stage},${SELECTORS.hero}`);
    root?.cwlController?.selectBlock?.(event.detail?.blockId || event.target.dataset.shopifyBlockId);
  });

  // Deliberate, tiny public surface for automated verification and future merge.
  window.CorsailleLivingWater = Object.freeze({
    version: '1.0.0',
    initialize,
    destroyScope,
    transitionTo(root, index) { return root?.cwlController?.goTo?.(index, true); },
    qaFrame(root, index, progress = 0.5, options = {}) {
      const controller = root?.cwlController;
      const outgoing = controller?.slides?.[controller.activeIndex];
      const incoming = controller?.slides?.[normalizeIndex(index, controller?.slides?.length || 1)];
      return controller?.transition?.previewFrame(outgoing, incoming, { progress, ...options });
    },
    qaClear(root) {
      const controller = root?.cwlController;
      const outgoing = controller?.slides?.[controller.activeIndex];
      const incoming = controller?.slides?.find((slide) => slide.classList.contains('is-incoming'));
      controller?.transition?.cleanup(outgoing, incoming);
      controller?.sync?.();
    }
  });
})();
